// Import icons
import dashboardFill from "@iconify/icons-eva/bar-chart-fill";
import pieChartFill from "@iconify/icons-eva/pie-chart-fill";
import pinFill from "@iconify/icons-eva/pin-fill";
import fileFill from "@iconify/icons-eva/file-fill";
import creditCardFill from "@iconify/icons-eva/credit-card-fill";
import flashFill from "@iconify/icons-eva/flash-fill";
import peopleFill from "@iconify/icons-eva/people-fill";
import syncFill from "@iconify/icons-eva/sync-fill";
import chargingStationFill from "@iconify/icons-eva/charging-fill";
import alertCircleFill from "@iconify/icons-eva/alert-circle-fill";
import { Icon } from "@iconify/react";
// mui components
import { Box, Avatar, Typography, Chip } from "@mui/material";

// constants
import { OCPI_COMMAND_TYPES } from "src/utils/ocpi.constants";


export const initialValues = {
  companyId: "",
  partnerId: null,
  partnerName: "",
  businessWebsite: "",
  contactEmail: "",
  partyId: "",
  countryCode: "",
  ocpiRole: "",
  ocpiVersion: [],
  step: -1,
  // Enhanced Technical Config
  baseUrl: "",
  token: "",
  clientCert: null,
  ipInput: "",
  ipWhitelist: [],
  requestsPerMinute: 60,
  burstLimit: 100,
  syncMode: "",
  syncInterval: 10,
  endpoints: [],
  // Credential Exchange
  remoteToken: "",
  remoteEndpoint: "",
  // Partner Verification
  verificationCode: "",
  verificationContact: "",
  // Credential Exchange Step Status
  credentialExchangeStatus: {
    handshakeStatus: "Pending",
    tokenStatus: "Pending",
    discoveryStatus: "Pending",
    validationStatus: "Pending",
    completed: false,
  },
  endpointsCount: 0,
};

export const getCommandsInitialValues = (commandType) => {
  const baseValues = {
    type: commandType,
    responseUrl: "https://cpo.example.com/ocpi/2.2/commands",
    notes: "",
  };

  switch (commandType) {
    case "START_SESSION":
      return {
        ...baseValues,
        locationId: "",
        evseUid: "",
        connectorId: "",
        authorizationReference: "",
      };

    case "STOP_SESSION":
      return {
        ...baseValues,
        sessionId: "",
      };

    case "UNLOCK_CONNECTOR":
      return {
        ...baseValues,
        locationId: "",
        evseUid: "",
        connectorId: "",
      };

    case "RESERVE_NOW":
      return {
        ...baseValues,
        locationId: "",
        evseUid: "",
        authorizationReference: "",
        reservationId: "",
      };

    case "CANCEL_RESERVATION":
      return {
        ...baseValues,
        reservationId: "",
      };

    default:
      return baseValues;
  }
};

export const steps = [
  "Company Selection",
  "Partner Info & Business Verification",
  "Enhanced Technical Configuration",
  "Credential Exchange Process",
  // "Enhanced Partner Verification",
  "Final Review & Activation",
];

export const exchangeSteps = [
  {
    title: "Initial Handshake",
    description:
      "Exchange basic party information and verify OCPI version compatibility",
    key: "handshakeStatus",
  },
  {
    title: "Endpoint Discovery",
    description: "Discover and validate all required OCPI endpoints",
    key: "discoveryStatus",
  },
  {
    title: "Token Generation & Exchange",
    description: "Generate secure tokens and establish mutual authentication",
    key: "tokenStatus",
  },
  {
    title: "Credential Validation",
    description: "Cross-validate credentials and test API connectivity",
    key: "validationStatus",
  },
];

export const technicalTests = [
  {
    key: "ocpi-versions",
    title: "OCPI Versions Endpoint",
    desc: "Verify /versions endpoint returns valid OCPI versions",
    status: "pending",
  },
  {
    key: "ssl-cert",
    title: "SSL Certificate Validation",
    desc: "Validate SSL certificate and security headers",
    status: "pending",
  },
  {
    key: "auth-token",
    title: "Authentication Token",
    desc: "Test token authentication on protected endpoints",
    status: "pending",
  },
  {
    key: "required-endpoints",
    title: "Required Endpoints Available",
    desc: "Check all required OCPI endpoints are accessible",
    status: "pending",
  },
  {
    key: "api-format",
    title: "API Response Format",
    desc: "Validate API responses conform to OCPI standards",
    status: "pending",
  },
  {
    key: "rate-limit",
    title: "Rate Limit Compliance",
    desc: "Test API rate limiting and throttling behavior",
    status: "pending",
  },
];

// Go-Live checklist items
export const checklist = [
  { label: "Credential exchange completed", checked: true },
  { label: "Partner verification passed", checked: true },
  { label: "Endpoints configured and tested", checked: true },
  { label: "Security settings configured", checked: true },
  { label: "Monitoring setup complete", checked: true },
];
// Health metrics (static for preview)
export const health = [
  {
    label: "Uptime",
    value: "99.50%",
    icon: "mdi:check-circle-outline",
    color: "success.main",
  },
  {
    label: "Avg Response Time",
    value: "180ms",
    icon: "mdi:check-circle-outline",
    color: "success.main",
    sub: "\u2199 -5ms from last hour",
  },
  {
    label: "Error Rate",
    value: "0.50%",
    icon: "mdi:check-circle-outline",
    color: "success.main",
    sub: "\u2191 +0.1% from yesterday",
  },
  {
    label: "SLA Compliance",
    value: "99.2%",
    icon: "mdi:check-circle-outline",
    color: "success.main",
  },
];

/**
 * Extracts request data for each step to send to backend.
 * @param {object} values - The form values object.
 * @returns {object} An object with step names as keys and request data as values.
 */
export function extractStepRequestData(values, currentStep) {
  const stepData = {
    1: {
      companyId: values.companyId,
    },
    2: {
      partner_name: values.partnerName,
      business_id: values.businessWebsite,
      contact_email: values.contactEmail,
      party_id: values.partyId,
      country_code: values.countryCode,
      ocpi_role: values.ocpiRole,
      ocpi_version: values.ocpiVersion,
      company_id: values.companyId,
    },
    3: {
      base_url: values.baseUrl,
      token: values.token,
      client_cert: values.clientCert,
      ip_address: values.ipWhitelist,
      requests_per_minute: values.requestsPerMinute,
      burst_limit: values.burstLimit,
      sync_mode: values.syncMode,
      sync_interval: values.syncInterval,
    },
    4: {
      remote_token: btoa(values.token),
      remote_endpoint: values.baseUrl,
    },
    5: {
      status: "Active",
    },
    6: {
      handshake_status: "In Progress",
      token_status: values.credentialExchangeStatus.tokenStatus,
      discovery_status: values.credentialExchangeStatus.discoveryStatus,
      validation_status: values.credentialExchangeStatus.validationStatus,
      completed: values.credentialExchangeStatus.completed,
    }
  };

  return stepData[currentStep + 1] || {};
}

export const Status = {
  PENDING: "Pending",
  ACTIVE: "Active",
  INACTIVE: "Inactive",
  IN_PROGRESS: "In Progress",
  COMPLETED: "Completed",
  FAILED: "Failed",
  VALID: "Valid",
  INVALID: "INVALID",
  // OCPI Command Statuses
  PROCESSING: "PROCESSING",
  CANCELLED: "CANCELLED",
  // OCPI Session Statuses
  SESSION_ACTIVE: "ACTIVE",
  SESSION_COMPLETED: "COMPLETED",
  SESSION_PENDING: "PENDING",
  SESSION_FAILED: "FAILED",
  CDR_PENDING: "PENDING",
  CDR_SENT: "SENT",
  CDR_FAILED: "FAILED",
  // OCPI Location Statuses
  LOCATION_ACTIVE: "AVAILABLE",
  LOCATION_INACTIVE: "OUTOFORDER",
  LOCATION_CHARGING: "CHARGING",
  LOCATION_INOPERATIVE: "INOPERATIVE",
  LOCATION_UNKNOWN: "UNKNOWN",
  LOCATION_RESERVED: "RESERVED",
  LOCATION_PLANNED: "PLANNED",
  LOCATION_REMOVED: "REMOVED",
  LOCATION_BLOCKED: "BLOCKED"
};

export const statusColorMap = {
  [Status.PENDING]: {
    color: "warning",
    bgcolor: "warning.lighter",
    text: "warning.dark",
  },
  [Status.ACTIVE]: {
    color: "primary",
    bgcolor: "primary.lighter",
    text: "primary.dark",
  },
  [Status.INACTIVE]: {
    color: "default",
    bgcolor: "grey.200",
    text: "text.secondary",
  },
  [Status.IN_PROGRESS]: {
    color: "primary",
    bgcolor: "primary.lighter",
    text: "primary.dark",
  },
  [Status.COMPLETED]: {
    color: "success",
    bgcolor: "success.lighter",
    text: "success.light",
  },
  [Status.FAILED]: {
    color: "error",
    bgColor: "error.light",
    text: "error.dark",
  },
  [Status.VALID]: {
    color: "primary",
    bgcolor: "primary.lighter",
    text: "primary.dark",
  },
  [Status.INVALID]: {
    color: "error",
    bgColor: "error.light",
    text: "error.light",
  },
  // OCPI Command Statuses
  [Status.PROCESSING]: {
    color: "info",
    bgcolor: "info.lighter",
    text: "info.dark",
  },
  [Status.CANCELLED]: {
    color: "default",
    bgcolor: "grey.200",
    text: "text.secondary",
  },
  // OCPI Session Statuses
  [Status.SESSION_ACTIVE]: {
    color: "primary",
    bgcolor: "primary.lighter",
    text: "primary.dark",
  },
  [Status.SESSION_COMPLETED]: {
    color: "primary",
    bgcolor: "primary.lighter",
    text: "primary.dark",
  },
  [Status.SESSION_PENDING]: {
    color: "warning",
    bgcolor: "warning.lighter",
    text: "warning.dark",
  },
  [Status.SESSION_FAILED]: {
    color: "error",
    bgcolor: "error.lighter",
    text: "error.dark",
  },
  [Status.CDR_PENDING]: {
    color: "warning",
    bgcolor: "warning.lighter",
    text: "warning.dark",
  },
  [Status.CDR_SENT]: {
    color: "success",
    bgcolor: "success.lighter",
    text: "success.dark",
  },
  [Status.CDR_FAILED]: {
    color: "error",
    bgcolor: "error.lighter",
    text: "error.dark",
  },
  // OCPI Location Statuses
  [Status.LOCATION_ACTIVE]: {
    color: "primary",
    bgcolor: "primary.lighter",
    text: "primary.darker",
  },
  [Status.LOCATION_INACTIVE]: {
    color: "error",
    bgcolor: "error.lighter",
    text: "error.dark",
  },
  [Status.LOCATION_CHARGING]: {
    color: "info",
    bgcolor: "info.lighter",
    text: "info.dark",
  },
  [Status.LOCATION_INOPERATIVE]: {
    color: "error",
    bgcolor: "error.lighter",
    text: "error.dark",
  },
  [Status.LOCATION_UNKNOWN]: {
    color: "default",
    bgcolor: "grey.200",
    text: "text.secondary",
  },
  [Status.LOCATION_RESERVED]: {
    color: "primary",
    bgcolor: "primary.lighter",
    text: "primary.dark",
  },
  [Status.LOCATION_PLANNED]: {
    color: "warning",
    bgcolor: "warning.lighter",
    text: "warning.dark",
  },
  [Status.LOCATION_REMOVED]: {
    color: "error",
    bgcolor: "error.lighter",
    text: "error.dark",
  },
  [Status.LOCATION_BLOCKED]: {
    color: "error",
    bgcolor: "error.lighter",
    text: "error.dark",
  }
};

export const statusIconMap = {
  [Status.PENDING]: {
    icon: "mdi:clock-outline",
    color: "warning",
  },
  [Status.INACTIVE]: {
    icon: "mdi:close-circle-outline",
    color: "default",
  },
  [Status.ACTIVE]: {
    icon: "mdi:check-circle-outline",
    color: "primary",
  },
  [Status.IN_PROGRESS]: {
    icon: "mdi:loading",
    color: "primary",
  },
  [Status.COMPLETED]: {
    icon: "mdi:check-circle",
    color: "primary",
  },
  [Status.FAILED]: {
    icon: "mdi:close-circle",
    color: "danger",
  },
  // OCPI Command Statuses
  [Status.PROCESSING]: {
    icon: "mdi:autorenew",
    color: "info",
  },
  [Status.CANCELLED]: {
    icon: "mdi:close-circle-outline",
    color: "default",
  },
  // OCPI Session Statuses
  [Status.SESSION_ACTIVE]: {
    icon: "eva:radio-button-on-fill",
    color: "success",
  },
  [Status.SESSION_COMPLETED]: {
    icon: "eva:checkmark-circle-2-fill",
    color: "primary",
  },
  [Status.SESSION_PENDING]: {
    icon: "eva:clock-fill",
    color: "warning",
  },
  [Status.SESSION_FAILED]: {
    icon: "eva:close-circle-fill",
    color: "error",
  },
  [Status.CDR_PENDING]: {
    icon: "eva:clock-fill",
    color: "warning",
  },
  [Status.CDR_SENT]: {
    icon: "eva:checkmark-circle-2-fill",
    color: "success",
  },
  [Status.CDR_FAILED]: {
    icon: "eva:close-circle-fill",
    color: "error",
  }
};

// Centralized function to get status color for MUI components
export const getStatusColor = (status) => {
  const normalizedStatus = status?.toUpperCase();
  const statusConfig = statusColorMap[normalizedStatus];

  if (statusConfig) {
    return statusConfig.color;
  }

  // Fallback for unknown statuses
  return "default";
};

// Centralized function to get status background color
export const getStatusBgColor = (status) => {
  const normalizedStatus = status?.toUpperCase();
  const statusConfig = statusColorMap[normalizedStatus];

  if (statusConfig) {
    return statusConfig.bgcolor;
  }

  // Fallback for unknown statuses
  return "grey.200";
};

// Centralized function to get status text color
export const getStatusTextColor = (status) => {
  const normalizedStatus = status?.toUpperCase();
  const statusConfig = statusColorMap[normalizedStatus];

  if (statusConfig) {
    return statusConfig.text;
  }

  // Fallback for unknown statuses
  return "text.secondary";
};

export const ocpiModules = [
  {
    title: "Dashboard",
    description: "Monitor OCPI operations and partner status",
    icon: dashboardFill,
    path: "/ocpi/dashboard",
    color: "#00d25b",
    status: "Active",
    features: [
      "Real-time monitoring",
      "Partner status tracking",
      "System health overview",
    ],
  },
  {
    title: "Analytics",
    description: "Comprehensive roaming and revenue analytics",
    icon: pieChartFill,
    path: "/ocpi/analytics",
    color: "#1976d2",
    status: "Active",
    features: [
      "Revenue analytics",
      "Transaction insights",
      "Performance metrics",
    ],
  },
  {
    title: "Locations",
    description: "Manage charging station locations and EVSEs",
    icon: pinFill,
    path: "/ocpi/locations",
    color: "#ed6c02",
    status: "Active",
    features: [
      "Location management",
      "EVSE monitoring",
      "Real-time availability",
    ],
  },
  {
    title: "Sessions (CDRs)",
    description: "Monitor active charging sessions and view charge detail records",
    icon: chargingStationFill,
    path: "/ocpi/module/sessions",
    color: "#ff9800",
    status: "Active",
    features: ["Active sessions", "Session monitoring", "Real-time status", "CDR details"],
  },
  {
    title: "Tariffs",
    description: "Manage pricing tariffs and charging rates",
    icon: creditCardFill,
    path: "/ocpi/module/tariffs",
    color: "#d32f2f",
    status: "Active",
    features: [
      "Pricing management",
      "Tariff configuration",
      "Rate optimization",
    ],
  },
  {
    title: "Tokens",
    description: "Manage authorization tokens for charging",
    icon: flashFill,
    path: "/ocpi/module/tokens",
    color: "#2e7d32",
    status: "Active",
    features: ["Token authorization", "Access control", "Security management"],
  },
  {
    title: "Commands",
    description: "Remote charging operations and control",
    icon: flashFill,
    path: "/ocpi/module/commands",
    color: "#c2185b",
    status: "Active",
    features: ["Remote operations", "Session control", "Command tracking"],
  },
  {
    title: "Partners",
    description: "Manage OCPI partner connections",
    icon: peopleFill,
    path: "/ocpi/partners",
    color: "#7b1fa2",
    status: "Active",
    features: [
      "Partner management",
      "Connection health",
      "Data synchronization",
    ],
  },
  // {
  //   title: "Synchronization",
  //   description: "Monitor and control OCPI data sync",
  //   icon: syncFill,
  //   path: "/ocpi-synchronization",
  //   color: "#455a64",
  //   status: "Active",
  //   features: ["Data synchronization", "Sync monitoring", "Error resolution"],
  // },
];

export const commandsHistoryColumns = [
  {
    field: 'commandUid',
    headerName: 'Command UID',
    flex: 1,
    minWidth: 150,
    renderCell: ({ row }) => row.commandUid || '-'
  },
  {
    field: 'type',
    headerName: 'Command Type',
    flex: 1,
    minWidth: 150,
    renderCell: ({ row }) => (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          px: 2,
          py: 0.5,
          borderRadius: 2,
          bgcolor: `${OCPI_COMMAND_TYPES.find(type => type.value === row.type)?.color}22`,
          color: OCPI_COMMAND_TYPES.find(type => type.value === row.type)?.color,
          fontWeight: 500,
          fontSize: 14,
          border: `1px solid ${OCPI_COMMAND_TYPES.find(type => type.value === row.type)?.color}55`,
          width: 'fit-content',
          minWidth: 120,
          justifyContent: 'center',
        }}
      >
        {row.type}
      </Box>
    )
  },
  {
    field: 'locationName',
    headerName: 'Location',
    flex: 1,
    minWidth: 120,
    renderCell: ({ row }) => row.locationName || '-'
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    minWidth: 120,
    renderCell: ({ row }) => (
      <Chip
        label={row.status}
        color={getStatusColor(row.status)}
        size="small"
        variant="outlined"
      />
    )
  },
  {
    field: 'updatedAt',
    headerName: 'Timestamp',
    flex: 1,
    minWidth: 200,
    renderCell: ({ row }) => (
      <Box>
        <Typography variant="body2">
          {row.createdAt ? new Date(row.createdAt).toLocaleString() : '-'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Executed: {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '-'}
        </Typography>
      </Box>
    )
  },
  {
    field: 'commandData',
    headerName: 'Command Data',
    flex: 1,
    minWidth: 250,
    width: 300,
    renderCell: ({ row }) => (
      <Box
        sx={{
          backgroundColor: 'grey.100',
          padding: 1,
          borderRadius: 1,
          fontSize: '0.75rem',
          fontFamily: 'monospace',
          overflow: 'hidden',
          maxWidth: '100%',
          height: 'auto',
          maxHeight: '100px',
          cursor: 'pointer',
          '&:hover': {
            overflow: 'auto',
            backgroundColor: 'grey.200'
          }
        }}
        title={row.commandData ? JSON.stringify(JSON.parse(row.commandData), null, 2) : '-'}
      >
        {row.commandData ? (
          <Box component="span" sx={{ 
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {JSON.stringify(JSON.parse(row.commandData))}
          </Box>
        ) : '-'}
      </Box>
    )
  },
  {
    field: 'result',
    headerName: 'Result',
    flex: 1,
    minWidth: 200,
    width: 250,
    renderCell: ({ row }) => (
      <Box
        sx={{
          backgroundColor: 'grey.100',
          padding: 1,
          borderRadius: 1,
          fontSize: '0.75rem',
          fontFamily: 'monospace',
          overflow: 'hidden',
          maxWidth: '100%',
          height: 'auto',
          maxHeight: '100px',
          cursor: 'pointer',
          '&:hover': {
            overflow: 'auto',
            backgroundColor: 'grey.200'
          }
        }}
        title={row.result ? JSON.stringify(JSON.parse(row.result), null, 2) : '-'}
      >
        {row.result ? (
          <Box component="span" sx={{ 
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {JSON.stringify(JSON.parse(row.result))}
          </Box>
        ) : '-'}
      </Box>
    )
  }
];
